import {
    Box,
    Divider,
    Flex,
    Input,
    Select,
    Text,
    Spinner,
    FormControl,
    FormLabel,
    InputGroup,
    InputRightElement,
} from "@chakra-ui/react";
import { FaChartLine, FaDownload } from "react-icons/fa";
import { PopupButton, PopupLayout } from "../../../PopupLayout/PopupLayout";
import Plot from "react-plotly.js";
import { useState, useEffect, useMemo } from "react";
import { getAvailableParameters } from "../../services/sensitivityAnalysisService";

const SensitivityAnalysisPopup = ({
    graph,
    econVars,
    isOpen,
    popupState,
    setPopupState,
    onSimulate,
    loading,
    setLoading,
    results,
    setResults,
}) => {
    // Parameter selection state
    const [parameterType, setParameterType] = useState("economic");
    const [selectedParameter, setSelectedParameter] = useState(null);

    // Range configuration
    const [minValue, setMinValue] = useState("");
    const [maxValue, setMaxValue] = useState("");
    const [steps, setSteps] = useState(10);

    // Output configuration
    const [returnType, setReturnType] = useState("NPV");

    // Get available parameters from graph
    const availableParameters = useMemo(() => {
        if (!graph || !econVars) return { stream: [], unit: [], economic: [], feedProduct: [] };
        return getAvailableParameters(graph, econVars);
    }, [graph, econVars]);

    // Get parameters for current type
    const currentParameters = useMemo(() => {
        return availableParameters[parameterType] || [];
    }, [availableParameters, parameterType]);

    // Reset selected parameter and results when type changes
    useEffect(() => {
        setSelectedParameter(null);
        setMinValue("");
        setMaxValue("");
        setResults(null);
    }, [parameterType]);

    // Update min/max with current value when parameter is selected
    useEffect(() => {
        if (selectedParameter) {
            const currentVal = selectedParameter.currentValue || 0;
            // Default to ±20% of current value, or 0-100 if current is 0
            if (currentVal !== 0) {
                const a = (currentVal * 0.8).toFixed(4);
                const b = (currentVal * 1.2).toFixed(4);
                setMinValue(parseFloat(a) < parseFloat(b) ? a : b);
                setMaxValue(parseFloat(a) < parseFloat(b) ? b : a);
            } else {
                setMinValue("0");
                setMaxValue("100");
            }
        }
    }, [selectedParameter]);

    const handleParameterSelect = (e) => {
        const paramLabel = e.target.value;
        const param = currentParameters.find(p => p.label === paramLabel);
        setSelectedParameter(param || null);
        if (param) {
            const currentVal = param.currentValue || 0;
            let newMin, newMax;
            if (currentVal !== 0) {
                const a = (currentVal * 0.8).toFixed(4);
                const b = (currentVal * 1.2).toFixed(4);
                newMin = parseFloat(a) < parseFloat(b) ? a : b;
                newMax = parseFloat(a) < parseFloat(b) ? b : a;
            } else {
                newMin = "0";
                newMax = "100";
            }
            setMinValue(newMin);
            setMaxValue(newMax);
            setLoading(true);
            onSimulate(param, parseFloat(newMin), parseFloat(newMax), parseInt(steps), returnType);
        }
    };

    const handleSimulate = () => {
        if (!selectedParameter || !minValue || !maxValue) {
            return;
        }

        setLoading(true);
        onSimulate(
            selectedParameter,
            parseFloat(minValue),
            parseFloat(maxValue),
            parseInt(steps),
            returnType
        );
    };

    const canSimulate = selectedParameter && minValue && maxValue && steps > 0;

    const handleExport = () => {
        if (!results || results.length === 0) return;

        const paramLabel = selectedParameter?.label || "Parameter";
        const csvContent = [
            [paramLabel, returnType].join(","),
            ...results.map(r => [r.paramValue, r.output].join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `sensitivity_${paramLabel.replace(/\s+/g, "_")}_${returnType}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* Collapsed button */}
            {isOpen && (
                <Box
                    position="absolute"
                    bottom="0"
                    left="4"
                    margin="4"
                    marginBottom="60px"
                    zIndex="1000"
                    borderRadius="10px"
                    boxShadow="lg"
                    bgColor="white"
                    padding="2"
                    cursor="pointer"
                    onClick={() => setPopupState(popupState === "popup" ? "button" : "popup")}
                    title="Sensitivity Analysis"
                >
                    <FaChartLine color="#1a58ab" size={30} />
                </Box>
            )}

            {/* Expanded popup */}
            {isOpen && popupState === "popup" && (
                <Box position="relative" zIndex={2000}>
                <PopupLayout
                    overflowY="auto"
                    maxHeight="90vh"
                    minWidth={results && results.length > 0 ? "550px" : "400px"}
                    pageX={80}
                    pageY={80}
                    title="Sensitivity Analysis"
                    closeFunction={() => setPopupState("button")}
                    buttons={
                        <>
                            {loading && <Spinner size="sm" mr={2} />}
                            <Select
                                value={returnType}
                                onChange={(e) => {
                                    const newReturnType = e.target.value;
                                    setReturnType(newReturnType);
                                    if (selectedParameter && minValue && maxValue && steps > 0) {
                                        setLoading(true);
                                        onSimulate(selectedParameter, parseFloat(minValue), parseFloat(maxValue), parseInt(steps), newReturnType);
                                    }
                                }}
                                width="120px"
                                size="sm"
                                borderRadius="10px"
                            >
                                <option value="NPV">NPV</option>
                                <option value="LCOP">LCOP</option>
                            </Select>
                            {results && results.length > 0 && (
                                <PopupButton onClick={handleExport}>
                                    <FaDownload />
                                </PopupButton>
                            )}
                            <PopupButton
                                disabled={loading || !canSimulate}
                                onClick={handleSimulate}
                            >
                                Analyze
                            </PopupButton>
                        </>
                    }
                >
                    <Box p={2}>
                        {/* Parameter Type Selection */}
                        <FormControl mb={4}>
                            <FormLabel fontSize="sm" fontWeight="600">
                                Parameter Type
                            </FormLabel>
                            <Select
                                value={parameterType}
                                onChange={(e) => setParameterType(e.target.value)}
                                size="sm"
                            >
                                <option value="economic">Economic Parameters</option>
                                <option value="feedProduct">Feed & Product Prices</option>
                                <option value="stream">Stream Parameters</option>
                                <option value="unit">Unit Parameters</option>
                            </Select>
                        </FormControl>

                        {/* Parameter Selection */}
                        <FormControl mb={4}>
                            <FormLabel fontSize="sm" fontWeight="600">
                                Parameter
                            </FormLabel>
                            <Select
                                value={selectedParameter?.label || ""}
                                onChange={handleParameterSelect}
                                placeholder="Select parameter"
                                size="sm"
                            >
                                {currentParameters.map((param, index) => (
                                    <option key={index} value={param.label}>
                                        {param.label}
                                        {param.currentValue !== undefined &&
                                            ` (current: ${param.currentValue})`}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Current Value Display */}
                        {selectedParameter && (
                            <Box mb={4} p={2} bg="gray.50" borderRadius="md">
                                <Text fontSize="sm" color="gray.600">
                                    Current Value:{" "}
                                    <Text as="span" fontWeight="600">
                                        {selectedParameter.currentValue}
                                        {selectedParameter.unit && ` ${selectedParameter.unit}`}
                                    </Text>
                                </Text>
                            </Box>
                        )}

                        {/* Range Configuration */}
                        <Flex gap={4} mb={4}>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">
                                    Min
                                </FormLabel>
                                <Input
                                    value={minValue}
                                    onChange={(e) => setMinValue(e.target.value)}
                                    type="number"
                                    size="sm"
                                    placeholder="Min value"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">
                                    Max
                                </FormLabel>
                                <Input
                                    value={maxValue}
                                    onChange={(e) => setMaxValue(e.target.value)}
                                    type="number"
                                    size="sm"
                                    placeholder="Max value"
                                />
                            </FormControl>
                            <FormControl width="120px">
                                <FormLabel fontSize="sm" fontWeight="600">
                                    Steps
                                </FormLabel>
                                <Input
                                    value={steps}
                                    onChange={(e) => setSteps(e.target.value)}
                                    type="number"
                                    size="sm"
                                    min={2}
                                    max={50}
                                />
                            </FormControl>
                        </Flex>

                        {/* Results Scatter Plot */}
                        {results && results.length > 0 && (
                            <>
                                <Divider my={4} borderWidth="1px" />
                                <Plot
                                    data={[
                                        {
                                            x: results.map((r) => r.paramValue),
                                            y: results.map((r) => r.output),
                                            type: "scatter",
                                            mode: "lines+markers",
                                            marker: { color: "#1a58ab", size: 6 },
                                            line: { color: "#1a58ab", width: 2 },
                                            name: returnType,
                                        },
                                    ]}
                                    layout={{
                                        width: 380,
                                        height: 220,
                                        title: {
                                            text: `${returnType} vs ${selectedParameter?.label || "Parameter"}`,
                                            font: { size: 12 },
                                        },
                                        xaxis: {
                                            title: {
                                                text: selectedParameter?.label || "Parameter Value",
                                                font: { size: 10 },
                                            },
                                            gridcolor: "#eee",
                                        },
                                        yaxis: {
                                            title: {
                                                text: returnType,
                                                font: { size: 10 },
                                            },
                                            gridcolor: "#eee",
                                        },
                                        margin: { l: 50, r: 20, t: 40, b: 40 },
                                        paper_bgcolor: "white",
                                        plot_bgcolor: "white",
                                    }}
                                    config={{
                                        displayModeBar: true,
                                        displaylogo: false,
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </PopupLayout>
                </Box>
            )}
        </>
    );
};

export default SensitivityAnalysisPopup;
